import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Search, Trash2, Edit, DollarSign, FileText } from 'lucide-react';
import { useCurrency, useAuth } from '@/App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/v1`;

export default function FixedExpensesPage() {
    const [expenses, setExpenses] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');

    const { formatPrice, formatPYG } = useCurrency();

    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [currentExpense, setCurrentExpense] = useState(null); // If null, creating; else editing
    const [formData, setFormData] = useState({
        description: '',
        amount_pyg: '',
        invoice_number: '',
        purchase_order_id: ''
    });

    const fetchExpenses = async () => {
        setIsLoading(true);
        try {
            const res = await axios.get(`${API}/fixed_expenses/`);
            setExpenses(res.data);
        } catch (error) {
            console.error("Error fetching fixed expenses:", error);
            toast.error("Error al cargar los gastos fijos");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchExpenses();
    }, []);

    const handleOpenDialog = (expense = null) => {
        if (expense) {
            setCurrentExpense(expense);
            setFormData({
                description: expense.description,
                amount_pyg: expense.amount_pyg,
                invoice_number: expense.invoice_number || '',
                purchase_order_id: expense.purchase_order_id || ''
            });
        } else {
            setCurrentExpense(null);
            setFormData({
                description: '',
                amount_pyg: '',
                invoice_number: '',
                purchase_order_id: ''
            });
        }
        setIsDialogOpen(true);
    };

    const handleSave = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                ...formData,
                amount_pyg: Number(formData.amount_pyg)
            };

            if (currentExpense) {
                await axios.put(`${API}/fixed_expenses/${currentExpense.id}`, payload);
                toast.success("Gasto actualizado correctamente");
            } else {
                await axios.post(`${API}/fixed_expenses/`, payload);
                toast.success("Gasto creado correctamente");
            }

            setIsDialogOpen(false);
            fetchExpenses();
        } catch (error) {
            console.error("Error saving fixed expense:", error);
            toast.error("Error al guardar el gasto");
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("¿Estás seguro de eliminar este gasto?")) return;
        try {
            await axios.delete(`${API}/fixed_expenses/${id}`);
            toast.success("Gasto eliminado");
            fetchExpenses();
        } catch (error) {
            console.error("Error deleting expense:", error);
            toast.error("Error al eliminar el gasto");
        }
    };

    const filteredExpenses = expenses.filter(ex =>
        ex.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (ex.invoice_number && ex.invoice_number.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (ex.purchase_order_id && ex.purchase_order_id.toLowerCase().includes(searchQuery.toLowerCase()))
    );

    const totalExpensesPYG = filteredExpenses.reduce((sum, ex) => sum + ex.amount_pyg, 0);

    if (isLoading) return <div className="p-8 text-center">Cargando gastos...</div>;

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <h1 className="text-3xl font-bold flex items-center gap-2" data-font="display">
                    <DollarSign className="size-6 text-muted-foreground" /> Gastos Fijos
                </h1>
                <Button onClick={() => handleOpenDialog()} className="bg-[hsl(var(--primary))] hover:bg-[#BFA774]">
                    <Plus className="mr-2 size-4" /> Nuevo Gasto
                </Button>
            </div>

            <Card>
                <CardHeader>
                    <div className="flex items-center gap-2">
                        <Search className="size-4 text-muted-foreground" />
                        <Input
                            placeholder="Buscar por descripción, factura o ID de Orden..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="max-w-sm"
                        />
                    </div>
                </CardHeader>
                <CardContent>
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Descripción</TableHead>
                                    <TableHead>Factura #</TableHead>
                                    <TableHead>Orden de Compra</TableHead>
                                    <TableHead className="text-right">Monto (PYG)</TableHead>
                                    <TableHead className="text-right">Acciones</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {filteredExpenses.map((expense) => (
                                    <TableRow key={expense.id}>
                                        <TableCell className="font-medium">{expense.description}</TableCell>
                                        <TableCell>{expense.invoice_number || '-'}</TableCell>
                                        <TableCell>
                                            {expense.purchase_order_id ? (
                                                <Badge variant="outline" className="font-mono text-xs">
                                                    {expense.purchase_order_id.substring(0, 8)}...
                                                </Badge>
                                            ) : '-'}
                                        </TableCell>
                                        <TableCell className="text-right font-semibold">{formatPYG(expense.amount_pyg)}</TableCell>
                                        <TableCell className="text-right">
                                            <Button variant="ghost" size="icon" onClick={() => handleOpenDialog(expense)}>
                                                <Edit className="size-4 text-primary" />
                                            </Button>
                                            <Button variant="ghost" size="icon" onClick={() => handleDelete(expense.id)}>
                                                <Trash2 className="size-4 text-destructive" />
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {filteredExpenses.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                                            No se encontraron gastos fijos.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </div>
                    <div className="mt-4 flex justify-end items-center gap-2">
                        <span className="font-bold text-muted-foreground">Total (PYG):</span>
                        <span className="text-xl font-bold">{formatPYG(totalExpensesPYG)}</span>
                    </div>
                </CardContent>
            </Card>

            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{currentExpense ? 'Editar Gasto' : 'Nuevo Gasto Fijo'}</DialogTitle>
                        <DialogDescription>Ingrese los detalles del gasto.</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleSave} className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="description">Descripción *</Label>
                            <Input
                                id="description"
                                value={formData.description}
                                onChange={e => setFormData({ ...formData, description: e.target.value })}
                                required
                                placeholder="Ej. Flete internacional"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="amount">Monto (PYG) *</Label>
                            <Input
                                id="amount"
                                type="number"
                                value={formData.amount_pyg}
                                onChange={e => setFormData({ ...formData, amount_pyg: e.target.value })}
                                required
                                min="0"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="invoice">Factura # (Opcional)</Label>
                            <Input
                                id="invoice"
                                value={formData.invoice_number}
                                onChange={e => setFormData({ ...formData, invoice_number: e.target.value })}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="po_id">ID Orden de Compra (Opcional)</Label>
                            <Input
                                id="po_id"
                                value={formData.purchase_order_id}
                                onChange={e => setFormData({ ...formData, purchase_order_id: e.target.value })}
                                placeholder="UUID de la orden"
                            />
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="ghost" onClick={() => setIsDialogOpen(false)}>Cancelar</Button>
                            <Button type="submit">Guardar</Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}
